import {useState, useMemo, useEffect} from 'react';
import {Link, Navigate, useNavigate} from 'react-router-dom';
import {format, formatDistanceToNow} from 'date-fns';

import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {DataTable, type Column} from '@common-web/components/data-table';

import {
  Inbox,
  Search,
  Building,
  LayoutList,
  SquareKanban,
  Clock,
  Loader2,
} from 'lucide-react';

import {
  getRfcStatusDisplayName,
  getStatusColor,
  getStatusIcon,
} from '@common-web/components/rfc/RfcStatus';
import {READY_FOR_CBS_STATUSES} from '@common-web/components/rfc/RfcSubmissionsList';
import {PageHeader} from '@common-web/components/page-header';
import {useDebounce} from '@common-web/hooks/use-debounce';
import {createMutationErrorHandler, trpc, type RouterOutput} from '@common-web/lib/trpc';
import {RfcStatus} from '@common/types/rfc';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useHasPermission} from '@common-web/hooks/use-permissions';

type CbsQueueRfc = RouterOutput['clients']['rfc']['listForAdmin']['data'][number];

const CBS_KANBAN_STATUSES = READY_FOR_CBS_STATUSES;

/**
 * Route guard for the CBS queue: the page (and its sidebar entry) is gated on
 * `rfc.cbs_queue`, so an internal user whose role lacks it is redirected home
 * even when navigating by URL. Frontend defense-in-depth only — the underlying
 * data stays reachable to internal admins through the RFC submissions list.
 */
export function CbsQueuePage() {
  const canAccessCbsQueue = useHasPermission(CODE_PERMISSIONS.rfc.cbsQueue.key);
  if (!canAccessCbsQueue) {
    return <Navigate to='/groups' replace />;
  }
  return <CbsQueuePageContent />;
}

function CbsQueuePageContent() {
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<RfcStatus | 'ALL'>(RfcStatus.MSA_SIGNED);
  const [clientFilter, setClientFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingRfcId, setPendingRfcId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = viewMode === 'kanban' ? 200 : 20;

  const debouncedSearch = useDebounce(searchQuery, 900);

  const statuses = statusFilter === 'ALL' ? [...READY_FOR_CBS_STATUSES] : [statusFilter];

  const rfcsQuery = trpc.clients.rfc.listForAdmin.useQuery({
    page: currentPage,
    limit: pageSize,
    sortKey: 'createdAt',
    sortDirection: 'asc',
    statuses,
    search: debouncedSearch || undefined,
    clientId: clientFilter !== 'ALL' ? clientFilter : undefined,
  });

  const clientsQuery = trpc.internal.client.list.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, debouncedSearch, viewMode, clientFilter]);

  const rfcData = rfcsQuery.data?.data ?? [];
  const pagination = rfcsQuery.data?.pagination;

  const groupedByStatus = useMemo(() => {
    const groups: Record<string, typeof rfcData> = {};
    CBS_KANBAN_STATUSES.forEach((s) => (groups[s] = []));
    rfcData.forEach((item) => {
      if (!item.status || !groups[item.status]) return;
      groups[item.status].push(item);
    });
    return groups;
  }, [rfcData]);

  const quickStartMutation = trpc.internal.onboarding.quickStartOnboarding.useMutation({
    onSuccess: (data) => {
      setPendingRfcId(null);
      if (data.createGroupStepId) {
        navigate(`/groups/new?onboardingId=${data.id}&stepId=${data.createGroupStepId}`);
      } else {
        navigate(`/onboarding/${data.id}`);
      }
    },
    onError: (error) => {
      setPendingRfcId(null);
      createMutationErrorHandler('Failed to start onboarding')(error);
    },
  });

  const getDetailUrl = (rfc: {id: string}) => `/rfc/admin/${rfc.id}`;

  const getCompanyName = (rfc: {companyName?: string | null}) =>
    rfc.companyName || 'No company name';

  const getWaitingSince = (rfc: {
    brokerApprovedAt?: Date | string | null;
    submittedAt?: Date | string | null;
    createdAt?: Date | string | null;
  }) => rfc.brokerApprovedAt ?? rfc.submittedAt ?? rfc.createdAt ?? null;

  const columns: Column<CbsQueueRfc>[] = [
    {
      id: 'company',
      header: 'Client & Company',
      render: (rfc) => (
        <div className='flex items-center space-x-3'>
          <Building className='h-4 w-4 text-brand-gray' />
          <div>
            <p className='font-medium text-brand-navy'>{getCompanyName(rfc)}</p>
            <p className='text-sm text-brand-gray'>{rfc.client.name}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      render: (rfc) => {
        const status = rfc.status as RfcStatus;
        return (
          <div className='flex items-center gap-2 text-black'>
            {getStatusIcon(status)}
            <Badge variant={getStatusColor(status)}>{getRfcStatusDisplayName(status)}</Badge>
          </div>
        );
      },
    },
    {
      id: 'waitingSince',
      header: 'Waiting Since',
      render: (rfc) => {
        const waitingSince = getWaitingSince(rfc);
        return waitingSince ? (
          <div className='flex items-center gap-2 text-sm text-brand-gray'>
            <Clock className='h-3 w-3' />
            <span title={format(new Date(waitingSince), 'MMM d, yyyy h:mm a')}>
              {formatDistanceToNow(new Date(waitingSince), {addSuffix: true})}
            </span>
          </div>
        ) : (
          <span className='text-sm text-brand-gray'>—</span>
        );
      },
    },
    {
      id: 'broker',
      header: 'Broker',
      render: (rfc) => (
        <span className='text-sm text-brand-gray'>{rfc.assignedBroker?.name || '—'}</span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      render: (rfc) => (
        <div className='flex items-center gap-3'>
          <Button
            size='sm'
            disabled={quickStartMutation.isPending && pendingRfcId === rfc.id}
            onClick={() => {
              setPendingRfcId(rfc.id);
              quickStartMutation.mutate({
                rfcSubmissionId: rfc.id,
                clientId: rfc.client.id,
                groupName: rfc.companyName || 'New Group',
              });
            }}
          >
            {pendingRfcId === rfc.id && quickStartMutation.isPending ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              'Build'
            )}
          </Button>
          <button
            onClick={() => navigate(getDetailUrl(rfc))}
            className='text-sm font-medium text-brand-blue hover:underline'
          >
            Manage
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='CBS Queue'
          description='Deals awaiting group creation handoff'
          icon={<Inbox className='h-6 w-6' />}
          isFetching={rfcsQuery.isFetching}
          isLoading={rfcsQuery.isLoading}
        />

        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          {/* Filters */}
          <Card className='border-gray-200 shadow-sm mb-6'>
            <CardContent className='pt-6'>
              <div className='flex flex-col md:flex-row gap-4'>
                <div className='flex-1 relative'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
                  <Input
                    placeholder='Search by company name...'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className='pl-10 border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                  />
                </div>
                <div className='w-48'>
                  <Select
                    value={statusFilter}
                    onValueChange={(value) => setStatusFilter(value as RfcStatus | 'ALL')}
                  >
                    <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
                      <SelectValue placeholder='All Statuses' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='ALL'>All CBS Statuses</SelectItem>
                      {READY_FOR_CBS_STATUSES.map((status) => (
                        <SelectItem key={status} value={status}>
                          {getRfcStatusDisplayName(status)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='w-48'>
                  <Select value={clientFilter} onValueChange={(value) => setClientFilter(value)}>
                    <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
                      <SelectValue placeholder='All Clients' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='ALL'>All Clients</SelectItem>
                      {clientsQuery.data?.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className='flex items-center gap-1 border border-gray-200 rounded-md p-1'>
                  <Button
                    variant='ghost'
                    size='icon'
                    className={`h-8 w-8 ${viewMode === 'table' ? 'bg-brand-navy text-white hover:bg-brand-navy' : ''}`}
                    onClick={() => setViewMode('table')}
                  >
                    <LayoutList className='w-full h-full' />
                  </Button>
                  <Button
                    variant='ghost'
                    size='icon'
                    className={`h-8 w-8 ${viewMode === 'kanban' ? 'bg-brand-navy text-white hover:bg-brand-navy' : ''}`}
                    onClick={() => setViewMode('kanban')}
                  >
                    <SquareKanban className='w-full h-full' />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Table View */}
          {viewMode === 'table' ? (
            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='bg-gray-50 border-b'>
                <CardTitle className='text-lg font-semibold text-brand-navy'>
                  CBS Queue ({pagination?.total || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className='p-6'>
                <div className={rfcsQuery.isFetching ? 'opacity-60' : ''}>
                  <DataTable<CbsQueueRfc>
                    columns={columns}
                    data={rfcData}
                    loading={rfcsQuery.isLoading}
                    error={
                      rfcsQuery.error
                        ? new Error(rfcsQuery.error.message || 'Failed to load CBS queue')
                        : null
                    }
                    emptyMessage={
                      searchQuery || statusFilter !== 'ALL' || clientFilter !== 'ALL'
                        ? 'No deals match your search or filters'
                        : 'No deals are waiting for CBS handoff'
                    }
                  />
                  {!!pagination && pagination.totalPages > 1 && (
                    <div className='flex items-center justify-between mt-4'>
                      <p className='text-sm text-brand-gray'>
                        Showing {(currentPage - 1) * pageSize + 1} to{' '}
                        {Math.min(currentPage * pageSize, pagination.total)} of {pagination.total}{' '}
                        results
                      </p>
                      <div className='flex space-x-2'>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={!pagination.hasPrev}
                        >
                          Previous
                        </Button>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={!pagination.hasNext}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Kanban View */
            <div className='-mx-2'>
              <div className='flex gap-4 overflow-x-auto px-2 pb-2'>
                {CBS_KANBAN_STATUSES.map((status) => {
                  const items = groupedByStatus[status] || [];
                  if (statusFilter !== 'ALL' && status !== statusFilter) return null;
                  return (
                    <div key={status} className='min-w-[320px] w-[320px]'>
                      <Card className='h-full border-gray-200 shadow-sm'>
                        <CardHeader className='py-3 bg-gray-50 border-b'>
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-2'>
                              {getStatusIcon(status)}
                              <CardTitle className='text-base font-semibold text-brand-navy'>
                                {getRfcStatusDisplayName(status)}
                              </CardTitle>
                            </div>
                            <Badge variant={getStatusColor(status)}>{items.length}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className='space-y-3 p-4'>
                          {items.length === 0 ? (
                            <div className='text-sm text-brand-gray py-6 text-center'>No items</div>
                          ) : (
                            items.map((rfc) => {
                              const waitingSince = getWaitingSince(rfc);
                              return (
                                <div
                                  key={rfc.id}
                                  className='border border-gray-200 rounded-md p-3 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer'
                                  onClick={() => navigate(getDetailUrl(rfc))}
                                >
                                  <div className='font-medium leading-snug text-brand-navy'>
                                    {getCompanyName(rfc)}
                                  </div>
                                  <div className='text-xs text-brand-gray mt-1'>
                                    {rfc.client.name}
                                  </div>
                                  {rfc.assignedBroker?.name && (
                                    <div className='text-xs text-brand-gray mt-0.5'>
                                      Broker: {rfc.assignedBroker.name}
                                    </div>
                                  )}
                                  <div className='flex items-center gap-1.5 text-xs text-brand-gray mt-3'>
                                    <Clock className='h-3 w-3' />
                                    {waitingSince
                                      ? formatDistanceToNow(new Date(waitingSince), {addSuffix: true})
                                      : '—'}
                                  </div>
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    className='mt-2 w-full text-xs font-medium bg-gray-50 text-brand-navy hover:bg-gray-100'
                                    disabled={quickStartMutation.isPending && pendingRfcId === rfc.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPendingRfcId(rfc.id);
                                      quickStartMutation.mutate({
                                        rfcSubmissionId: rfc.id,
                                        clientId: rfc.client.id,
                                        groupName: rfc.companyName || 'New Group',
                                      });
                                    }}
                                  >
                                    {pendingRfcId === rfc.id && quickStartMutation.isPending ? (
                                      <Loader2 className='h-3 w-3 animate-spin' />
                                    ) : (
                                      'Build'
                                    )}
                                  </Button>
                                </div>
                              );
                            })
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
